import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Wifi, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { SEOHead, breadcrumbSchema } from '../components/seo/SEOHead';
import { downloadQRCodePNG, downloadQRCodeSVG } from '../utils/qrHelpers';
import { trackEvent } from '../components/analytics/GoogleAnalytics';

export const WiFiQRPage: React.FC = () => {
  const [ssid, setSSID] = useState('');
  const [password, setPassword] = useState('');
  const [encryption, setEncryption] = useState<'WPA' | 'WEP' | 'nopass'>('WPA');
  const [hidden, setHidden] = useState(false);
  const [size, setSize] = useState(300);

  const qrContent = `WIFI:T:${encryption};S:${ssid};P:${password};H:${hidden ? 'true' : 'false'};;`;
  const isValid = ssid.length > 0;

  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', url: 'https://freeqrcodenow.com/' },
    { name: 'WiFi QR Code Generator', url: 'https://freeqrcodenow.com/wifi-qr-code-generator' },
  ]);

  return (
    <>
      <SEOHead
        title="Free WiFi QR Code Generator | Create WiFi QR Codes Instantly"
        description="Generate free WiFi QR codes for instant network sharing. Create QR codes for WPA, WEP, or open networks. No login required. Download as PNG or SVG."
        keywords="wifi qr code, wifi qr code generator, free wifi qr, network qr code, wireless qr generator"
        structuredData={breadcrumbs}
      />

      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-4">
            <Wifi className="inline h-10 w-10 mr-3 text-green-600" />
            Free WiFi QR Code Generator
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Create QR codes for instant WiFi network sharing. No typing passwords – just scan and connect!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Input Section */}
          <Card>
            <CardHeader>
              <CardTitle>WiFi Network Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                label="Network Name (SSID)"
                placeholder="MyWiFiNetwork"
                value={ssid}
                onChange={(e) => setSSID(e.target.value)}
              />

              <Input
                label="Password"
                type="password"
                placeholder="Enter WiFi password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <div>
                <label className="block text-sm font-medium mb-2">Encryption Type</label>
                <select
                  value={encryption}
                  onChange={(e) => setEncryption(e.target.value as any)}
                  className="w-full p-2 border border-input rounded-md bg-background"
                >
                  <option value="WPA">WPA/WPA2</option>
                  <option value="WEP">WEP</option>
                  <option value="nopass">No Password</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="hidden"
                  checked={hidden}
                  onChange={(e) => setHidden(e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="hidden" className="text-sm font-medium">
                  Hidden Network
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">QR Code Size: {size}px</label>
                <input
                  type="range"
                  min="200"
                  max="500"
                  value={size}
                  onChange={(e) => setSize(parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>

          {/* Preview Section */}
          <Card>
            <CardHeader>
              <CardTitle>QR Code Preview</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center space-y-6">
              {isValid ? (
                <>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <QRCodeSVG
                      value={qrContent}
                      size={size}
                      level="M"
                      includeMargin={true}
                    />
                  </div>

                  <div className="flex flex-wrap gap-3 justify-center">
                    <Button onClick={() => {
                      downloadQRCodePNG(qrContent, { size, foregroundColor: '#000000', backgroundColor: '#ffffff', errorCorrectionLevel: 'M', marginSize: 4 }, false, 'wifi-qr-code.png');
                      trackEvent('download', 'WiFi QR', 'PNG');
                    }}>
                      <Download className="h-4 w-4 mr-2" />
                      Download PNG
                    </Button>
                    <Button variant="outline" onClick={() => {
                      downloadQRCodeSVG(qrContent, { size, foregroundColor: '#000000', backgroundColor: '#ffffff', errorCorrectionLevel: 'M', marginSize: 4 }, 'wifi-qr-code.svg');
                      trackEvent('download', 'WiFi QR', 'SVG');
                    }}>
                      <Download className="h-4 w-4 mr-2" />
                      Download SVG
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <Wifi className="h-16 w-16 mb-4 text-gray-300" />
                  <p>Enter WiFi network details to generate QR code</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* SEO Content */}
        <div className="mt-12 prose prose-lg max-w-none">
          <h2>How to Create a WiFi QR Code</h2>
          <p>
            Creating a WiFi QR code is simple and free. Just follow these steps:
          </p>
          <ol>
            <li>Enter your WiFi network name (SSID)</li>
            <li>Add your network password</li>
            <li>Select the encryption type (WPA/WPA2 is most common)</li>
            <li>Choose if your network is hidden</li>
            <li>Click download to save your QR code</li>
          </ol>

          <h2>Why Use WiFi QR Codes?</h2>
          <p>
            WiFi QR codes make it incredibly easy to share your network with guests. Instead of spelling out
            long passwords, visitors can simply scan the code and connect instantly. Perfect for:
          </p>
          <ul>
            <li>Homes and offices</li>
            <li>Cafes and restaurants</li>
            <li>Hotels and Airbnbs</li>
            <li>Events and conferences</li>
            <li>Co-working spaces</li>
          </ul>

          <h2>Best Practices for WiFi QR Codes</h2>
          <p>
            To get the most out of your WiFi QR codes:
          </p>
          <ul>
            <li>Print them in high quality for easy scanning</li>
            <li>Place them in visible, accessible locations</li>
            <li>Update codes when you change your password</li>
            <li>Consider using a guest network for security</li>
            <li>Test scanning before distributing</li>
          </ul>
        </div>
      </div>
    </>
  );
};


